import React, { useState } from "react";
import { AiTwotoneStar } from "react-icons/ai";
import { GoDotFill } from "react-icons/go";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { colors } from "../../utils/Colors";
import { formatNumberWithCommas } from "../../utils/helperFunctions";
import RoutesPath from "../../utils/Paths";
import Avatar from "../common/Avatar";
import api from "../../api/axios";

/**
 * @typedef {import("../../types/server").JobDetailData} Props
 */
const JobCard2 = (/** @type {Props} **/ props) => {
  const [applyLoading, setApplyLoading] = useState(false);

  const handleJobClick = async () => {
    // External jobs: POST /track then open apply URL
    if (props.is_external) {
      if (applyLoading) return;
      setApplyLoading(true);
      try {
        const res = await api.post("/external-jobs/track", {
          job_id:   props._id,
          page_url: window.location.href,
          source:   "web",
        });
        window.open(res?.data?.redirect_url || props.external_apply_url, "_blank", "noopener,noreferrer");
      } catch (_) {
        window.open(props.external_apply_url, "_blank", "noopener,noreferrer");
      } finally {
        setApplyLoading(false);
      }
      return;
    }

    // Internal jobs: open detail page as before
    if (props.jobType === "employer") {
      window.open(`${RoutesPath.manageJobs}/${props._id}/detail`, "_blank");
    } else {
      window.open(`${RoutesPath.manageProjects}/${props._id}/detail`, "_blank");
    }
  };

  return (
    <JobCard2Style colors={colors} onClick={handleJobClick} style={{ cursor: applyLoading ? "wait" : "pointer" }}>
      <img
        className="bg-img"
        src={props.attachments[0]?.imgUrl}
        alt="Background"
      />
      <div className="card-body">
        <div className="card-body-header">
          {props.is_external && (
            <span className="external-badge">{props.source_type || "external"}</span>
          )}
        </div>
        <div>
          <div className="d-flex flex-column">
            <div className="d-flex gap-2">
              <p>
                <Avatar
                  imgSrc={props.createdBy?.profileImg?.imgUrl}
                  name={props.createdBy?.username}
                  width={40}
                  height={40}
                />
              </p>

              <div
                style={{ fontWeight: 600 }}
                className="overflow-hidden avatar-username"
              >
                {props.createdBy?._id && (
                  <Link
                    className="text-truncate username-txt"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    to={`/profile/${props.createdBy?._id}?mode=buyer`}
                    target="_blank"
                  >
                    {props.createdBy?.username}
                  </Link>
                )}
              </div>
            </div>
            <div className="card-title">
              <p>
                {props.description?.length > 56
                  ? `${props.description?.slice(0, 56)}...`
                  : props.description}
              </p>
            </div>
          </div>
          <div className="card-body-footer">
            <div
              className="d-flex gap-2 align-items-center"
              style={{ color: "gray", fontSize: "16px" }}
            >
              <div className="d-flex gap-2">
                <div className="d-flex gap-2">
                  <p>{props.workType === "isOnline" ? "Online" : "Onsite"}</p>
                </div>
                <div>
                  <GoDotFill size={8} />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <p>
                    ${formatNumberWithCommas(props.budgetMin)} - $
                    {formatNumberWithCommas(props.budgetMax)}
                  </p>
                </div>
              </div>

              {props.createdBy.rating > 0 ? (
                <div className="rating" style={{ marginLeft: "16px" }}>
                  <AiTwotoneStar className="star-icon" />
                  <p>{props.createdBy.rating}</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </JobCard2Style>
  );
};
const JobCard2Style = styled.div`
  position: relative;
  width: 280px;
  height: 260px;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12);
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    transform: scale(1.03);
  }
  .username-txt {
    color: white !important;
    font-weight: 400;
  }
  .avatar-username {
    &:hover {
      .username-txt {
        text-decoration: underline !important;
      }
    }
  }
  .bg-img {
    width: 100%;
    height: 100%;
    z-index: -1;
    object-fit: cover;
  }
  .card-body {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 10px;
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 1) 25%,
      rgba(0, 0, 0, 0) 100%
    );
  }
  h4,
  h6,
  p {
    color: white;
  }
  h6 {
    text-shadow: 0 0 3px ${({ colors }) => colors.color.darkGrey};
  }
  .card-body-header {
    display: flex;
    align-items: center;
    text-align: center;
    justify-content: space-between;
  }
  .card-profile {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  .fav-icon-bg {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${({ colors }) => colors.color.white};
    width: 27px;
    height: 27px;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
  }
  .fav-icon {
    width: 20px;
    height: 20px;

    color: ${({ colors }) => colors.color.primary};
  }
  h6 {
    margin: 0;
  }
  h4 {
    font-size: 16px;
    margin: 0 !important;
  }
  .avatar {
    border: 1px solid ${({ colors }) => colors.color.lightGrey};
    border-radius: 50% !important;
    width: 40px;
    height: 40px;
    overflow: hidden;
  }
  .avatar img {
    width: 40px;
    height: 40px;
    object-fit: cover;
  }
  .card-title {
    margin: 5px 0;
    padding-bottom: 10px;
    border-bottom: 0.5px solid ${({ colors }) => colors.color.lightGrey};
  }
  .card-body-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .rating {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .star-icon {
    fill: gold;
    width: 20px;
    height: 20px;
  }
  .price {
    font-size: 18px;
  }
  .filled {
    color: red;
    fill: red;
  }
`;

export default JobCard2;

// ═══════════════════════════════════════════════════════════════════════════════
// ExternalJobCard — for third-party / aggregated low-level jobs
// Calls POST /track before opening the external company URL so every click is
// recorded with user identity, device, browser and page context.
// Falls back to the direct apply URL if the API call fails (network error, etc.)
// ═══════════════════════════════════════════════════════════════════════════════

export const ExternalJobCard = ({ job }) => {
  const [loading, setLoading] = useState(false);

  if (!job) return null;

  const salaryLabel = () => {
    if (!job.salary_min && !job.salary_max) return null;
    const type =
      job.salary_type === "hourly" ? "/hr" :
      job.salary_type === "monthly" ? "/mo" : "/yr";
    if (job.salary_min && job.salary_max) return `$${job.salary_min}–$${job.salary_max}${type}`;
    if (job.salary_max) return `Up to $${job.salary_max}${type}`;
    return `From $${job.salary_min}${type}`;
  };

  const handleApply = async () => {
    setLoading(true);
    try {
      // POST to tracking endpoint — auth token is added automatically by api client
      const res = await api.post("/external-jobs/track", {
        job_id:   job._id,
        page_url: window.location.href,
        source:   "web",
      });
      const redirectUrl = res?.data?.redirect_url || job.external_apply_url;
      window.open(redirectUrl, "_blank", "noopener,noreferrer");
    } catch (_) {
      // Tracking failed — still open the apply URL so the user is never blocked
      window.open(job.external_apply_url, "_blank", "noopener,noreferrer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ExternalCardWrapper>
      <div className="card-header">
        {job.company_logo && (
          <img src={job.company_logo} alt={job.company_name} className="company-logo" />
        )}
        <div className="company-info">
          <p className="company-name">{job.company_name || "Company"}</p>
          <p className="job-location">
            {job.location?.address || [job.city, job.state].filter(Boolean).join(", ")}
          </p>
        </div>
        {/* <span className="source-badge">{job.source_type}</span> */}
      </div>

      <h3 className="job-title">{job.title}</h3>

      <div className="job-meta">
        {job.employment_type && <span className="meta-chip">{job.employment_type}</span>}
        {salaryLabel() && <span className="meta-chip salary">{salaryLabel()}</span>}
      </div>

      {job.description && (
        <p className="job-desc">
          {job.description.slice(0, 180)}
          {job.description.length > 180 ? "…" : ""}
        </p>
      )}

      <div className="card-footer">
        <button
          className="apply-external-btn"
          onClick={handleApply}
          disabled={loading}
        >
          {loading ? "Opening…" : "🔗 Apply on Company Website"}
        </button>
      </div>
    </ExternalCardWrapper>
  );
};

const ExternalCardWrapper = styled.div`
  background: #fff;
  border: 1.5px solid #e8f0fe;
  border-radius: 12px;
  padding: 16px;
  width: 320px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  display: flex;
  flex-direction: column;
  gap: 10px;

  .card-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }
  .company-logo {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    object-fit: contain;
    border: 1px solid #eee;
  }
  .company-info { flex: 1; }
  .company-name  { font-size: 0.82rem; font-weight: 600; color: #555; margin: 0; }
  .job-location  { font-size: 0.78rem; color: #888; margin: 0; }
  .source-badge  {
    font-size: 0.68rem;
    background: #e8f0fe;
    color: #3367d6;
    border-radius: 20px;
    padding: 2px 8px;
    text-transform: uppercase;
    font-weight: 700;
    white-space: nowrap;
  }
  .job-title {
    font-size: 1rem;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0;
    line-height: 1.3;
  }
  .job-meta { display: flex; gap: 6px; flex-wrap: wrap; }
  .meta-chip {
    font-size: 0.73rem;
    background: #f0f4f8;
    color: #444;
    border-radius: 20px;
    padding: 2px 10px;
    font-weight: 500;
  }
  .meta-chip.salary { background: #e6f9f3; color: #1a8a60; }
  .job-desc { font-size: 0.82rem; color: #666; margin: 0; line-height: 1.5; }
  .card-footer { margin-top: auto; }
  .apply-external-btn {
    display: block;
    width: 100%;
    padding: 10px 0;
    background: linear-gradient(135deg, #0f3460, #1a8a60);
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 0.86rem;
    font-weight: 700;
    text-align: center;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover:not(:disabled) { opacity: 0.88; }
    &:disabled { opacity: 0.55; cursor: not-allowed; }
  }
`;

